import React from 'react'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import Copy from 'transfix-ui/components/Copy'
import { Box, Typography } from 'transfix-ui/core'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { ISpotRate } from 'apps/marketplace/store/amQuoting/types'
import { brandColors, Table, TableBody, TableCell, TableRow, Tooltip } from 'transfix-ui'
import { IPriceRequestDetailsFragment } from '../../../graphql/PriceRequestDetailsFragment'
import { getMilesTooltip } from '../../../utils/getMilesTooltipText'
import { formatPuDelDates } from '../../utils/formatFocusPanelContents'
import { formatExternalId } from '../../utils/formatHistoricalQuotesTableContents'
import { BidStatus } from '../BidStatus'
import { EquipmentIconCell } from '../EquipmentIconCell'

interface IQuoteDetailsTableProps {
  spotRate: Partial<ISpotRate> & {
    metadata: ISpotRate['metadata']
  }
  priceRequest: IPriceRequestDetailsFragment
}

const useStyles = makeStyles(theme => ({
  align: {
    verticalAlign: 'middle',
    paddingLeft: '4px',
  },
  table: {
    backgroundColor: 'transparent',
    marginTop: theme.spacing(1.5),
  },
  header: { color: brandColors.coolGray5 },
  tableCell: {
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
    paddingLeft: '0px !important',
  },
  tooltip: {
    display: 'flex',
    alignItems: 'center',
    mb: 'auto',
  },
}))

export function QuoteDetailsTable({ priceRequest, spotRate }: IQuoteDetailsTableProps) {
  const classes = useStyles()
  const {
    metadata: { multiStopLegDatObjects },
  } = spotRate

  const shipperMiles = priceRequest.pricingResponse?.datMiles
    ? `${priceRequest.pricingResponse.datMiles.toString()}`
    : 'N/A'
  const formattedExternalId = formatExternalId(priceRequest.externalId)
  const externalId = formattedExternalId || 'N/A'
  const sqtObjectiveFourEnabled = useFeatureFlag('sqtObjectiveFour')
  const copyExternalId = priceRequest.externalId ? priceRequest.externalId : 'N/A'
  const tooltip = getMilesTooltip(multiStopLegDatObjects)
  const totalDatMiles = multiStopLegDatObjects?.reduce((n, { miles }) => n + (miles || 0), 0)

  return (
    <Table className={classes.table}>
      <TableRow className={classes.header}>
        <TableCell className={classes.tableCell}>PU/DEL Date</TableCell>
        <TableCell className={classes.tableCell}>Total DAT Miles</TableCell>
        <TableCell className={classes.tableCell}>Equipment</TableCell>
        {sqtObjectiveFourEnabled && <TableCell className={classes.tableCell}>Drop Type</TableCell>}
        <TableCell className={classes.tableCell}>Price Request ID</TableCell>
        <TableCell className={classes.tableCell}>Shipment ID (External)</TableCell>
        <TableCell className={classes.tableCell}>Bid Status</TableCell>
      </TableRow>
      <TableBody>
        <TableRow>
          <TableCell className={classes.tableCell}>
            {formatPuDelDates(priceRequest.pickupStartDate, priceRequest.deliveryStartDate)}
          </TableCell>
          <TableCell className={classes.tableCell}>
            {`${totalDatMiles || shipperMiles} mi`}
            {tooltip.length > 0 && (
              <span className={classes.align}>
                <Tooltip position='top' effect='solid' trigger={<InfoIcon size='medium' />}>
                  {tooltip.map((text, idx) => {
                    const id = idx + 1
                    return (
                      <Box key={id} className={classes.tooltip}>
                        <Box>
                          <Typography variant='caption'>{text}</Typography>
                        </Box>
                      </Box>
                    )
                  })}
                </Tooltip>
              </span>
            )}
          </TableCell>
          <TableCell className={classes.tableCell}>
            <EquipmentIconCell equipmentId={priceRequest.equipmentId} showLabel />
          </TableCell>
          {sqtObjectiveFourEnabled && (
            <TableCell className={classes.tableCell}>
              {`${priceRequest.pickupDropTrailer ? 'Drop' : 'Live'} - ${
                priceRequest.deliveryDropTrailer ? 'Drop' : 'Live'
              }`}
            </TableCell>
          )}
          <TableCell className={classes.tableCell}>
            <span>
              {priceRequest.id}
              <Copy copyText={priceRequest.id} useIcon alignClass='left' />
            </span>
          </TableCell>
          <TableCell className={classes.tableCell}>
            <span>
              {externalId}
              <Copy copyText={copyExternalId} useIcon alignClass='left' />
            </span>
          </TableCell>
          <TableCell className={classes.tableCell}>
            <BidStatus status={null} />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}
